// client/src/pages/ResetPasswordPage.js
import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Alert, Button, Stack, TextField } from '@mui/material';
import AuthLayout from '../components/AuthLayout';
import { API_BASE_URL } from '../utils/media';

const MIN_PASSWORD_LENGTH = 8;

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  // Kept in memory and taken out of the address bar, so the link can't leak through
  // browser history or to other sites.
  const [token] = useState(() => searchParams.get('token') || '');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    window.history.replaceState(null, '', window.location.pathname);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Your password needs at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (password !== confirm) {
      setError("Those passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Could not change your password.');
      setDone(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const footer = !token || done
    ? null
    : <Link to="/forgot-password" style={{ color: 'inherit', fontWeight: 700 }}>Request a new link</Link>;

  return (
    <AuthLayout eyebrow="Reset your password" title="Choose a new password" footer={footer}>
      {done && (
        <Stack spacing={2}>
          <Alert severity="success">Your password has been changed.</Alert>
          <Button component={Link} to="/login" variant="contained" size="large">Sign in</Button>
        </Stack>
      )}
      {!done && !token && (
        <Stack spacing={2}>
          <Alert severity="error">This reset link isn't complete. Please request a new one.</Alert>
          <Button component={Link} to="/forgot-password" variant="contained" size="large">Request a new link</Button>
        </Stack>
      )}
      {!done && token && (
        <Stack component="form" onSubmit={handleSubmit} spacing={2.25}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="New password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            required fullWidth autoComplete="new-password" helperText={`At least ${MIN_PASSWORD_LENGTH} characters`}
          />
          <TextField
            label="Confirm new password" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
            required fullWidth autoComplete="new-password"
          />
          <Button type="submit" variant="contained" size="large" disabled={loading}>
            {loading ? 'Saving…' : 'Change password'}
          </Button>
        </Stack>
      )}
    </AuthLayout>
  );
};

export default ResetPasswordPage;
